import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpIcon, DownloadIcon, FileTextIcon, ImageIcon, PaperclipIcon, UploadIcon, XIcon } from 'lucide-react';
import type { CitizenRequest, RequestAttachment, RequestMessage } from '../../data/demandes';
const ease = [0.23, 1, 0.32, 1] as const;
const filledForm: RequestAttachment = {
  name: 'vehicle-damage-claim-completed.pdf',
  type: 'pdf',
  size: '212 KB'
};
function AttachmentRow({
  file,
  tone
}: {
  file: RequestAttachment;
  tone: 'light' | 'dark';
}) {
  const Icon = file.type === 'image' ? ImageIcon : FileTextIcon;
  return <div className={`mt-3 flex items-center gap-3 rounded-2xl border px-3 py-2.5 ${tone === 'dark' ? 'border-white/20 bg-white/10' : 'border-slate-200 bg-white'}`} data-id="element-965" data-mp="39o01xiysksic">
      <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${tone === 'dark' ? 'bg-white/15 text-white' : 'bg-ville-mist text-ville-deep'}`} aria-hidden="true" data-id="element-966" data-mp="1f8xjg585uaox">
        <Icon className="h-4 w-4" data-id="element-967" data-mp="1k9ler33f9doy" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-968" data-mp="1lfk6h50b9yko">
        <span className={`block truncate text-sm font-bold ${tone === 'dark' ? 'text-white' : 'text-ville-navy'}`} data-id="element-969" data-mp="1lglhj9hbul2e">
          {file.name}
        </span>
        <span className={`block text-xs ${tone === 'dark' ? 'text-white/60' : 'text-slate-500'}`} data-id="element-970" data-mp="1bpqyedzdo1l6">{file.size}</span>
      </span>
      <button type="button" aria-label={`Download ${file.name}`} className={`grid h-9 w-9 shrink-0 place-items-center rounded-full transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${tone === 'dark' ? 'text-white/70 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-ville-navy'}`} data-id="element-971" data-mp="17oyorjmlfuod">
        <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-972" data-mp="1qctwalrm4c91" />
      </button>
    </div>;
}
export function RequestMessages({
  request
}: {
  request: CitizenRequest;
}) {
  const [messages, setMessages] = useState<RequestMessage[]>(request.messages);
  const [draft, setDraft] = useState('');
  const [pendingFile, setPendingFile] = useState<RequestAttachment | null>(null);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const idRef = useRef(0);
  useEffect(() => {
    setMessages(request.messages);
    setDraft('');
    setPendingFile(null);
  }, [request.id, request.messages]);
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [draft]);
  const awaiting = [...messages].reverse().find(message => message.author === 'ville' && message.awaiting);
  const send = () => {
    const body = draft.trim();
    if (!body && !pendingFile) return;
    idRef.current += 1;
    setMessages(current => [...current, {
      id: `local-${idRef.current}`,
      author: 'citoyen',
      authorName: 'You',
      sentAt: 'Just now',
      body: body || 'Here is the document you asked for.',
      attachment: pendingFile ?? undefined
    }]);
    setDraft('');
    setPendingFile(null);
  };
  return <section aria-labelledby="messages-titre" className="rounded-3xl border border-slate-200" data-id="element-973" data-mp="2w97bjncgdmum">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-4" data-id="element-974" data-mp="2rbp11sisezki">
        <div data-id="element-975" data-mp="357j0lkxo8cky">
          <h2 id="messages-titre" className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-976" data-mp="3181t6lyop5d5">
            Messages
          </h2>
          <p className="mt-0.5 text-sm text-slate-500" data-id="element-977" data-mp="3f71ky2hhu0dj">
            Talk to {request.service} about {request.reference}.
          </p>
        </div>
        <span className="rounded-full bg-ville-mist px-3 py-1 text-xs font-bold text-ville-deep" data-id="element-978" data-mp="3a6m2wxm9o4p8">
          Reply within 2 business days
        </span>
      </div>

      {awaiting && <div className="border-b border-slate-100 bg-ville-sand px-6 py-4" data-id="element-979" data-mp="3jz1n81l1d5da">
          <p className="text-sm font-extrabold text-ville-navy" data-id="element-980" data-mp="1tydg3k4k56j">
            {awaiting.awaiting === 'document' ? 'The Town is waiting on a document from you' : 'The Town is waiting on a detail from you'}
          </p>
          <p className="mt-1 text-sm text-slate-600" data-id="element-981" data-mp="1r9iv01azlh87">
            {awaiting.awaiting === 'document' ? 'Fill in the form attached to the last message, then return it here.' : 'Reply to the last message so the crew can carry on.'}
          </p>
          {awaiting.awaiting === 'document' && <button type="button" onClick={() => setPendingFile(filledForm)} className="mt-3 inline-flex items-center gap-2 rounded-full bg-ville-navy px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-982" data-mp="250dx8uqzd02t">
              <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-983" data-mp="1maxqvqsm6deh" />
              Attach the completed form
            </button>}
        </div>}

      <ol role="log" aria-live="polite" className="space-y-4 px-6 py-6" data-id="element-984" data-mp="2spzwtjb9lhbc">
        {messages.map(message => message.author === 'citoyen' ? <motion.li key={message.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="flex flex-col items-end" data-id="element-985" data-mp="2xo9t5x8q6nyj">
              <div className="max-w-[85%] rounded-3xl rounded-br-lg bg-ville-navy px-5 py-3.5 text-[15px] leading-relaxed text-white" data-id="element-986" data-mp="2y3q2mm5di7e4">
                {message.body}
                {message.attachment && <AttachmentRow file={message.attachment} tone="dark" data-id="element-987" data-mp="2p3a47r3hlqg4" />}
              </div>
              <p className="mt-1.5 pr-1 text-xs text-slate-400" data-id="element-988" data-mp="2k4gods0q9ncs">{message.sentAt}</p>
            </motion.li> : <motion.li key={message.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="flex flex-col items-start" data-id="element-989" data-mp="2f5dv14u415kn">
              <div className="max-w-[88%] rounded-3xl rounded-bl-lg border border-slate-200 bg-white px-5 py-3.5" data-id="element-990" data-mp="dcpmqh2lzry0">
                <p className="text-xs font-extrabold uppercase tracking-wider text-ville-deep" data-id="element-991" data-mp="9cxa9ta3h1nd">
                  {message.authorName}
                </p>
                <p className="mt-1.5 text-[15px] leading-relaxed text-ville-navy" data-id="element-992" data-mp="27mebmz6nfe1a">{message.body}</p>
                {message.attachment && <AttachmentRow file={message.attachment} tone="light" data-id="element-993" data-mp="1tvhk7bogl2vt" />}
                {message.awaiting === 'document' && <p className="mt-2 text-xs font-semibold text-ville-indigo" data-id="element-994" data-mp="1xwusrtixrk6f">Form to complete and return</p>}
                {message.awaiting === 'info' && <p className="mt-2 text-xs font-semibold text-ville-indigo" data-id="element-995" data-mp="2ljt1u64q7pub">Reply expected</p>}
              </div>
              <p className="mt-1.5 pl-1 text-xs text-slate-400" data-id="element-996" data-mp="2cl7p4rlxr51g">{message.sentAt}</p>
            </motion.li>)}
      </ol>

      <div className="border-t border-slate-100 px-4 pb-4 pt-3 sm:px-6" data-id="element-997" data-mp="2hj98ldn9x6dg">
        <form onSubmit={event => {
        event.preventDefault();
        send();
      }} className="rounded-[24px] border border-slate-200 px-4 pb-3 pt-3.5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-998" data-mp="115sjz0jkp6v9">
          <label htmlFor="message-ville" className="sr-only" data-id="element-999" data-mp="xmtpxakr1axq">
            Write a message to {request.service}
          </label>
          <textarea id="message-ville" ref={areaRef} rows={1} value={draft} onChange={event => setDraft(event.target.value)} onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            send();
          }
        }} placeholder="Write a message to the Town…" className="block max-h-40 w-full resize-none border-0 bg-transparent px-1 text-base leading-relaxed text-ville-navy placeholder:text-slate-400 focus:outline-none" data-id="element-1000" data-mp="tm020h7b3w4u" />

          {pendingFile && <div className="mt-2 flex items-center gap-2 rounded-full bg-ville-indigo-soft py-1.5 pl-3 pr-1.5 text-xs font-bold text-ville-indigo" data-id="element-1001" data-mp="op9ljsrlayiw">
              <FileTextIcon className="h-4 w-4" aria-hidden="true" data-id="element-1002" data-mp="3sbi3adci7bzp" />
              <span className="truncate" data-id="element-1003" data-mp="3nd6tg2m7l1hh">{pendingFile.name}</span>
              <button type="button" onClick={() => setPendingFile(null)} aria-label="Remove the attached file" className="grid h-6 w-6 shrink-0 place-items-center rounded-full hover:bg-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1004" data-mp="3ncx0q79s4g83">
                <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1005" data-mp="3br080u3gnnk2" />
              </button>
            </div>}

          <div className="mt-1.5 flex items-center justify-between gap-3" data-id="element-1006" data-mp="3gs65f582fyzx">
            <button type="button" onClick={() => setPendingFile(filledForm)} aria-label="Attach a file" className="grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1007" data-mp="3kt9jpnagtmz1">
              <PaperclipIcon className="h-5 w-5" aria-hidden="true" data-id="element-1008" data-mp="3bovxz5y3wm0" />
            </button>
            <button type="submit" disabled={draft.trim().length === 0 && !pendingFile} aria-label="Send the message" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ville-lime text-ville-navy transition-[transform,opacity] duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:translate-y-0" data-id="element-1009" data-mp="8cmc667eagkv">
              <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-1010" data-mp="1iwhf4qveihru" />
            </button>
          </div>
        </form>
      </div>
    </section>;
}